import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, KeyRound, ScanBarcode, Tag, Layers, WifiOff, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const FEATURES = [
  { icon: ScanBarcode, title: "Vende más fácil", text: "Todo está pensado para que cada venta sea rápida, sencilla y fluida." },
  { icon: Layers, title: "Todo en un solo lugar", text: "Encuentra lo que necesitas y realiza tu operación de forma clara y ordenada." },
  { icon: Tag, title: "Hecho para Rubi", text: "Tecnología desarrollada alrededor de nuestra operación y nuestra forma de atender." },
  { icon: WifiOff, title: "Siempre operando", text: "Aún sin internet, continúa trabajando. RubiPOS se sincroniza automáticamente al recuperar conexión." },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.15fr_1fr] bg-brand-canvas" data-testid="login-page">
      <section className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16 grid-dots overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-brand-amber/10 blur-3xl pointer-events-none" />
        <img src="/rubipos-logo-t.png" alt="RubiPOS" className="h-14 w-auto self-start animate-fade-up" data-testid="login-brand-logo" />

        <div className="max-w-xl relative">
          <div className="eyebrow mb-4 animate-fade-up" style={{ animationDelay: "60ms" }}>Punto de venta inteligente</div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight text-brand-ink leading-[1.05] animate-fade-up" style={{ animationDelay: "120ms" }}>
            La tecnología que transforma <span className="text-brand-amber">nuestra forma de vender</span>.
          </h1>
          <p className="mt-6 text-base text-brand-muted leading-relaxed max-w-lg animate-fade-up" style={{ animationDelay: "180ms" }}>
            RubiPOS fue creado especialmente para Rubi Perfumería: una experiencia más rápida, simple y confiable para que tengas todo lo que necesitas en cada venta.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mt-10">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="surface-card p-4 flex gap-3 animate-fade-up hover:-translate-y-0.5 hover:shadow-lift transition-[transform,box-shadow] duration-200" style={{ animationDelay: `${240 + i * 60}ms` }}>
                <div className="w-9 h-9 rounded-xl bg-brand-amber-soft border border-brand-amber-line flex items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4 text-brand-amber-dark" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm text-brand-ink">{f.title}</div>
                  <div className="text-xs text-brand-muted mt-0.5 leading-relaxed">{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-6 text-xs text-brand-muted animate-fade-up" style={{ animationDelay: "560ms" }} data-testid="login-system-status">
          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 text-emerald-500 dot-live" /> Sistema disponible</span>
          <span className="text-brand-line">•</span><span>Operación continua</span>
          <span className="text-brand-line">•</span><span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-amber" /> Sincronización automática</span>
        </div>
      </section>

      <section className="relative flex items-center justify-center p-6 sm:p-10 bg-brand-ink dark-grid overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-[62%] pointer-events-none select-none animate-fade-in" style={{ animationDuration: "1.6s", WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0) 100%)", maskImage: "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 40%, rgba(0,0,0,0) 100%)" }} data-testid="login-bg-photo"><img src="/sucursal-rubi.webp" alt="" aria-hidden="true" className="w-full h-full object-cover object-[70%_35%] opacity-[0.30] blur-[2px] scale-110 grayscale-[0.35] saturate-[0.7]" /></div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/50 via-transparent to-brand-ink/90 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(11,12,16,0.85)_100%)] pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-brand-amber/10 blur-3xl pointer-events-none" />
        <form onSubmit={onSubmit} className="relative w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden mb-8 inline-flex bg-white rounded-2xl px-4 py-3">
            <img src="/rubipos-logo.png" alt="RubiPOS" className="h-9 w-auto" />
          </div>
          <div className="mb-8">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-amber">Acceso al mostrador</div>
            <h2 className="font-display text-3xl font-extrabold text-white mt-2">Iniciar turno</h2>
            <p className="text-sm text-white/55 mt-2">Ingresa con tu cuenta para comenzar tu turno.</p>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-4 top-4 text-white/40" />
              <input
                data-testid="login-email-input"
                type="email"
                placeholder="Correo electrónico"
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-white/[0.06] border border-white/10 text-white placeholder:text-white/35 outline-none focus:border-brand-amber focus:bg-white/[0.09] transition-colors duration-150"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-4 top-4 text-white/40" />
              <input
                data-testid="login-password-input"
                type="password"
                placeholder="Contraseña"
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-white/[0.06] border border-white/10 text-white placeholder:text-white/35 outline-none focus:border-brand-amber focus:bg-white/[0.09] transition-colors duration-150"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 text-sm text-rose-200 bg-rose-500/15 border border-rose-400/30 rounded-xl px-4 py-3 animate-pop">
              {error}
            </div>
          )}

          <button data-testid="login-submit-button" type="submit" disabled={loading} className="btn-amber w-full h-12 mt-6 text-base">
            {loading ? "Entrando…" : "Entrar a RubiPOS"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>

          <div className="mt-10 flex flex-col gap-1 text-[11px] text-white/40">
            <span>Desarrollado por Fragancias Rubi SAS de CV</span>
            <span className="text-white/60 font-semibold">Más agilidad. Más control. Una mejor experiencia.</span>
          </div>
        </form>
      </section>
    </div>
  );
}
